/**
 * Cliente da Ingestion_API pública (R19.3) — o Control_Panel consome **apenas**
 * estes endpoints, nunca recursos internos.
 *
 * Contrato (verificado nos handlers `makepub_api.handler`):
 *   - `POST /jobs`            body `{ source_format }`, header `x-api-key`
 *                             → 201 `{ job_id, upload_url, expires_at }`
 *   - `PUT <upload_url>`      envia os bytes do arquivo direto ao S3 (presigned)
 *   - `GET /jobs/{id}`        header `x-api-key`
 *                             → 200 `{ status, attempt_count, last_error }`
 *   - `GET /jobs/{id}/download` header `x-api-key`
 *                             → 200 `{ job_id, download_url, expires_at }`
 *                             (409 `JobNotReady` enquanto não está DONE)
 *
 * Erros do backend chegam como `{ error, message }` com status HTTP estável
 * (401/404/409/429/400/500). Traduzimos tudo para `ApiError` (código + status),
 * e falhas de rede para `NetworkError`, para a UI reagir de forma resiliente
 * (R19.8) sem vazar detalhes crus.
 */

import type { JobStatus } from "./status";

/** Resposta de `POST /jobs`. */
export interface CreateJobResponse {
  readonly job_id: string;
  readonly upload_url: string;
  readonly expires_at: string;
}

/** Resposta de `GET /jobs/{id}`. */
export interface JobStatusResponse {
  readonly status: JobStatus;
  readonly attempt_count: number;
  readonly last_error: string | null;
}

/** Resposta de `GET /jobs/{id}/download`. */
export interface DownloadResponse {
  readonly job_id: string;
  readonly download_url: string;
  readonly expires_at: string;
}

/** Erro de aplicação retornado pela API (`{ error, message }` + status HTTP). */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
  }
}

/** Falha de comunicação (offline, DNS, CORS, timeout, PUT no S3, etc.). */
export class NetworkError extends Error {
  readonly cause?: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = "NetworkError";
    this.cause = cause;
  }
}

/** Injeção de `fetch` (default global) para testabilidade. */
export type FetchLike = typeof fetch;

export interface ApiClientOptions {
  readonly baseUrl: string;
  readonly apiKey: string;
  readonly fetchImpl?: FetchLike;
}

const JSON_HEADERS = { "content-type": "application/json" } as const;

async function parseError(response: Response): Promise<ApiError> {
  let code = "HttpError";
  let message = `Erro ${response.status}.`;
  try {
    const body = (await response.json()) as { error?: string; message?: string };
    if (body && typeof body === "object") {
      if (typeof body.error === "string" && body.error) {
        code = body.error;
      }
      if (typeof body.message === "string" && body.message) {
        message = body.message;
      }
    }
  } catch {
    // Corpo não-JSON: mantém o code/mensagem genéricos por status.
  }
  return new ApiError(code, message, response.status);
}

/** Cliente da Ingestion_API. Instância leve, criada a partir das configurações. */
export class MakePubApiClient {
  private readonly baseUrl: string;
  private readonly apiKey: string;
  private readonly fetchImpl: FetchLike;

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.apiKey = options.apiKey;
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  private authHeaders(): Record<string, string> {
    return { "x-api-key": this.apiKey };
  }

  private async request(path: string, init: RequestInit): Promise<Response> {
    let response: Response;
    try {
      response = await this.fetchImpl(`${this.baseUrl}${path}`, init);
    } catch (cause) {
      throw new NetworkError(
        "Não foi possível conectar ao serviço. Verifique sua conexão e tente novamente.",
        cause,
      );
    }
    return response;
  }

  /** `POST /jobs` — cria o job e obtém a URL de upload (R1.1/R1.7). */
  async createJob(sourceFormat: string): Promise<CreateJobResponse> {
    const response = await this.request("/jobs", {
      method: "POST",
      headers: { ...JSON_HEADERS, ...this.authHeaders() },
      body: JSON.stringify({ source_format: sourceFormat }),
    });
    if (!response.ok) {
      throw await parseError(response);
    }
    return (await response.json()) as CreateJobResponse;
  }

  /**
   * `PUT <upload_url>` — envia os bytes do arquivo direto ao S3 (presigned).
   *
   * O upload não usa o header `x-api-key` (a autorização está na assinatura da
   * URL). Enviamos o `content-type` do arquivo quando disponível.
   */
  async uploadFile(uploadUrl: string, file: Blob, contentType?: string): Promise<void> {
    let response: Response;
    try {
      response = await this.fetchImpl(uploadUrl, {
        method: "PUT",
        headers: contentType ? { "content-type": contentType } : undefined,
        body: file,
      });
    } catch (cause) {
      throw new NetworkError(
        "Falha ao enviar o arquivo. Verifique sua conexão e tente novamente.",
        cause,
      );
    }
    if (!response.ok) {
      throw new ApiError(
        "UploadFailed",
        "O envio do arquivo não foi concluído. Tente novamente.",
        response.status,
      );
    }
  }

  /** `GET /jobs/{id}` — status atual do job (R14.5). */
  async getJob(jobId: string): Promise<JobStatusResponse> {
    const response = await this.request(`/jobs/${encodeURIComponent(jobId)}`, {
      method: "GET",
      headers: this.authHeaders(),
    });
    if (!response.ok) {
      throw await parseError(response);
    }
    return (await response.json()) as JobStatusResponse;
  }

  /** `GET /jobs/{id}/download` — URL de download do EPUB final (R14.1). */
  async getDownloadUrl(jobId: string): Promise<DownloadResponse> {
    const response = await this.request(`/jobs/${encodeURIComponent(jobId)}/download`, {
      method: "GET",
      headers: this.authHeaders(),
    });
    if (!response.ok) {
      throw await parseError(response);
    }
    return (await response.json()) as DownloadResponse;
  }
}
